<template>
  <div v-show="ready === true">
    <div
      ref="content"
      :style="boxStyle()"
      class="mt-group"
      :class="[
        $style.box,
        {[$style.transition]: !isSizing},
        {[$style.boxOverflowGradient]: hasOverflow && settings.gradient === true && !isOpen},
      ]"
    >
      <slot />
    </div>
    <slot
      v-if="hasOverflow"
      name="trigger"
      :toggle="toggleOpen"
      :title="getTriggerTitle()"
    >
      <div :class="['mt-flex', `mt-flex--${settings.triggerPos}`]" :style = "{'margin-top': '0.25rem'}">
        <button
          :id="$vId(this, 'toggle-open')"
          type="button"
          class="mt-link mt-link--undecorated"
          @click="toggleOpen"
        >
          {{ getTriggerTitle() }}
        </button>
      </div>
    </slot>
  </div>
</template>

<script type="text/ecmascript-6">
import { Element } from 'src/utils/helper/element'

export default {
  name: 'FouExpand',

  props: {
    open: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object
    },
    triggerTitle: {
      type: String,
      default: 'More'
    },
    triggerTitleOpen: {
      type: String,
      default: 'Less'
    },
    rows: {
      type: [String ,Number],
      default: 2
    }
  },

  data () {
    return {
      isOpen: false,
      isSizing: false,
      clone: null,
      hasOverflow: false,
      settings: {
        gradient: true,
        minHeight: null,
        triggerPos: 'start' // or 'end'
      },
      ready: false,
      timer: null
    }
  },

  created () {
    if (this.options) {
      this.settings = Object.assign(this.settings, this.options)
    }
    this.isOpen = this.open
    window.addEventListener('resize', this.set)
    this.$events?.$on('fou:expand:update', () => {
      clearTimeout(this.timer)
      this.timer = setTimeout(this.set, 50)
    })
  },

  mounted () {
    /**
     * Calculation starts after render.
     */
  },

  beforeUnmount () {
    /**
     * Remove events.
     */
    window.removeEventListener('resize', this.set)
    this.$events.$off('fou:expand:update', this.set)
  },

  methods: {
    boxStyle () {
      /**
       * Calculate the height of the item.
       * In case of sizing or closed, apply fullHeight.
       */
      let h = 'auto'
      if (this.clone && !this.isOpen && !this.isSizing && this.hasOverflow) {
        h = this.calcMinHeight()
        h = `${h}px`
      }
      return {
        height: h
      }
    },
    toggleOpen () {
      this.isOpen = !this.isOpen
    },
    getTriggerTitle () {
      return this.isOpen
        ? this.triggerTitleOpen
        : this.triggerTitle
    },
    createClone () {
      /**
       * Append a clone of the content to body and hide it somewhere veeery far away.
       * Since one never knows where in the DOM the component will be placed,
       * one can not be really sure to make reliable calculation on its dimensions.
       * I.e. the component could be placed in a parent with display: none, etc.
       * The tricky thing is to get the correct width.
       * So for that there is fou:expand:update event, to update width by parents witdh.
       */
      const content = this.$refs.content
      if (content) {
        if (this.clone) { document.body?.removeChild(this.clone) }
        this.clone = content.cloneNode(true)
        const parent = this.$parent?.$el || document.body
        const parentWidth = Element(parent).outerWidth()
        this.clone.removeAttribute('style')
        this.clone.style.position = 'absolute'
        this.clone.style.left = '-9999999px'
        this.clone.style.top = '-9999999px'
        this.clone.style.width = `${parentWidth}px`
        document.body.appendChild(this.clone)
      }
    },
    calcMinHeight () {
      return this.settings.minHeight
        ? parseInt(this.settings.minHeight)
        : (24 * this.rows)
    },
    calcFullHeight () {
      return Element(this.clone).outerHeight()
    },
    set () {
      /**
       * Set height and tell template if it has overflow.
       */
      this.createClone()
      if (this.clone && this.clone.style.width !== '0px') {
        this.hasOverflow = (this.calcFullHeight() - this.calcMinHeight()) > 0
        if (!this.hasOverflow) {
          this.isOpen = this.open
        }
        this.ready = true
      }
    }
  }
}
</script>

<style lang="scss" module>
@import 'mpl-css/lib/assets/global/variables/colors';
@import 'mpl-css/lib/assets/global/functions/index';

.box {
  overflow: hidden;
  position: relative;
}

.boxOverflowGradient {
  &.expanded {
    &::after {
      display: none;
    }
  }
}

.transition {
  transition: height 0.25s ease-out;
  &.expanded {
    transition: height 0.25s ease-in;
  }
}
</style>
